/**
 * Policies & Compliance — live script deployment status from device_commands.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { DeviceIdentityCell } from '../ui/DeviceIdentityCell';

type DeploymentRow = {
  command_id: string;
  device_id: string;
  device_name?: string;
  hostname?: string | null;
  policy_key?: string | null;
  policy_title?: string;
  status?: string;
  outcome?: string;
  exit_code?: number | null;
  error_message?: string | null;
  result_preview?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  executed_at?: string | null;
  completed_at?: string | null;
  source?: string | null;
};

const OUTCOME_STYLES: Record<string, string> = {
  succeeded: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  running: 'bg-sky-50 text-sky-800 border-sky-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
};

function formatWhen(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const PolicyDeploymentStatusPanel: React.FC = () => {
  const [rows, setRows] = useState<DeploymentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await apiService.getPolicyDeploymentStatus({
        limit: 100,
        outcome: outcomeFilter || undefined,
      });
      setRows(Array.isArray(data?.items) ? data.items : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load deployment status');
      setRows([]);
      setTotal(0);
    } finally {
      setBusy(false);
    }
  }, [outcomeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const hasActive = rows.some((r) => r.outcome === 'pending' || r.outcome === 'running');
    if (!hasActive) return undefined;
    const id = window.setInterval(() => {
      void load();
    }, 8000);
    return () => window.clearInterval(id);
  }, [rows, load]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-500">
          Outcome{' '}
          <select
            className="ml-1 rounded border border-slate-200 px-2 py-1 text-sm"
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <button
          type="button"
          className="text-xs rounded border border-slate-200 bg-white px-2 py-1 hover:bg-slate-50"
          onClick={() => void load()}
          disabled={busy}
        >
          {busy ? 'Refreshing…' : 'Refresh'}
        </button>
        <span className="text-xs text-slate-400">{total} deployment{total === 1 ? '' : 's'}</span>
      </div>

      {error && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            <th className="py-2">Policy</th>
            <th>Device</th>
            <th>Outcome</th>
            <th>Operator</th>
            <th>Queued</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !busy && (
            <tr>
              <td colSpan={6} className="py-8 text-center text-slate-500">
                No policy deployments yet. Deploy a workstation or mobile policy to see status here.
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const style = OUTCOME_STYLES[r.outcome || ''] || 'bg-slate-50 text-slate-700 border-slate-200';
            const open = expanded === r.command_id;
            return (
              <React.Fragment key={r.command_id}>
                <tr className="border-b border-slate-100 align-top">
                  <td className="py-2">
                    <div className="font-medium text-slate-800">{r.policy_title || r.policy_key || 'Policy'}</div>
                    {r.policy_key && <div className="text-xs text-slate-400">{r.policy_key}</div>}
                  </td>
                  <td className="py-2">
                    <DeviceIdentityCell
                      device={{
                        device_id: r.device_id,
                        name: r.device_name,
                        hostname: r.hostname || undefined,
                      }}
                    />
                  </td>
                  <td className="py-2">
                    <span className={`inline-block rounded border px-2 py-0.5 text-xs capitalize ${style}`}>
                      {r.outcome || r.status || 'unknown'}
                    </span>
                    {typeof r.exit_code === 'number' && (
                      <div className="text-xs text-slate-400 mt-0.5">exit {r.exit_code}</div>
                    )}
                  </td>
                  <td className="py-2 text-slate-600">{r.created_by || '—'}</td>
                  <td className="py-2 text-slate-600 whitespace-nowrap">{formatWhen(r.created_at)}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="text-xs text-slate-600 underline"
                      onClick={() => setExpanded(open ? null : r.command_id)}
                    >
                      {open ? 'Hide' : 'Details'}
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <td colSpan={6} className="px-3 py-2 text-xs text-slate-700">
                      <div className="grid md:grid-cols-3 gap-2 mb-2">
                        <div>Status: {r.status || '—'}</div>
                        <div>Executed: {formatWhen(r.executed_at)}</div>
                        <div>Completed: {formatWhen(r.completed_at)}</div>
                      </div>
                      {r.error_message && (
                        <div className="mb-2 text-red-700">Error: {r.error_message}</div>
                      )}
                      <pre className="whitespace-pre-wrap break-words max-h-40 overflow-auto rounded border border-slate-200 bg-white p-2">
                        {r.result_preview || '(no output)'}
                      </pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PolicyDeploymentStatusPanel;
